/**
 * Browser-cache repair for a build asset that loads but is broken.
 *
 * Why this exists (KAN-512): a user sat on a white page, and every reload put
 * the same error back with a fresh reference. The server was healthy — all
 * three replicas served this very build, every hashed chunk was present and
 * came byte-identical off the CDN, and not a single asset request from that
 * user even reached nginx during the failure. The broken copy was in *his own
 * browser cache*.
 *
 * Nothing in the existing self-healing can get out of that: `/assets/*` is
 * served `immutable, max-age=1y`, so a reload never revalidates, and the
 * stale-build check (KAN-196) correctly answers "same build" and hands the
 * router a retry — which pulls the very same broken bytes out of the cache
 * again. The loop is closed and the user is stuck until they clear the cache
 * by hand.
 *
 * The way out is a request that is *specified* to overwrite what the cache
 * holds: `fetch(url, { cache: 'reload' })` bypasses the stored response and
 * stores the fresh one under the same URL. So we rewrite the cached entries
 * for the assets this document actually loaded, then reload — the next load
 * gets fresh bytes for the same URLs, and the repair outlives this tab.
 *
 * Deliberately *not* done by appending a cache-busting query: that only ever
 * reaches the top-level dynamic import, because a chunk's own static imports
 * resolve relative to its path and drop the query — the broken entry would
 * survive under its original URL and bite again in the next tab.
 */
import { stampReloadReason } from '@/lib/telemetry/page-reload';

/** Shared with nothing else: this cooldown guards its own reload only. */
const REFRESH_STAMP_KEY = '9brains.asset-cache-refresh-at';

/**
 * One repair per tab per cooldown. The reload it triggers lands on the same
 * route, so a chunk that is broken *on the server* (rather than in the cache)
 * would otherwise reload forever; after the cooldown declines, the error page
 * takes over — with a reference the user can quote.
 */
const REFRESH_COOLDOWN_MS = 10 * 60_000;

/**
 * Hard deadline on the refetch. The reload must happen even if the network
 * hangs: a user staring at "checking" forever is strictly worse than one who
 * reloads onto an unrepaired cache and gets the error page.
 */
const REFRESH_DEADLINE_MS = 5_000;

/**
 * Ceiling on how many assets one repair pulls — an emergency brake, not a
 * selection. A single `/chat` load already pulls ~172 hashed assets (~5.3 MB
 * raw, ~1.6 MB over the wire), so anything tighter would cut into the very
 * page we are repairing; the cap only bites after a lot of navigation, and
 * `collectAssetUrls` orders newest-first so what it drops is what the document
 * touched longest ago.
 */
const MAX_ASSETS = 200;

/**
 * Resource timing keeps 250 entries by default and then **silently drops every
 * further one** — with ~172 assets on the first chat load, a bit of navigation
 * is enough to lose exactly the newest entries, which is where the failing
 * chunk lives. Raising the buffer is what keeps the repair aimed at the right
 * files.
 */
const RESOURCE_TIMING_BUFFER = 600;

/**
 * Call once at startup (main.tsx), before any route chunk is imported.
 */
export function initAssetCacheRefresh(): void {
  try {
    performance.setResourceTimingBufferSize(RESOURCE_TIMING_BUFFER);
  } catch {
    // Not supported — collection falls back to whatever the default buffer
    // kept, plus the DOM and the error message.
  }
}

/** Test-only: forget this document's cooldown between cases. */
export function _resetAssetCacheRefreshForTests(): void {
  refreshedInThisDocument = false;
}

/** Second guard, for a repair asked for twice within the same document. */
let refreshedInThisDocument = false;

/**
 * Claim the one repair this tab is allowed to make.
 *
 * The cooldown has to survive the reload it guards — an in-memory flag is gone
 * the moment the document is replaced, and the failing route comes back with
 * the retry counter reset, so the next error would claim a repair again. That
 * is a reload loop, and it fires fastest exactly where nobody would look for
 * it: a browser that *accepts* `setItem` and stores nothing (private mode,
 * embedded webviews).
 *
 * So the write is read back, and no storage means no repair: without a way to
 * count the reload we do not make it, and the error page takes over — which is
 * exactly what such a browser gets today.
 */
function claimRefresh(): boolean {
  if (refreshedInThisDocument) return false;
  const now = Date.now();
  try {
    const last = Number(sessionStorage.getItem(REFRESH_STAMP_KEY) ?? '0');
    if (Number.isFinite(last) && now - last < REFRESH_COOLDOWN_MS) return false;
    sessionStorage.setItem(REFRESH_STAMP_KEY, String(now));
    if (sessionStorage.getItem(REFRESH_STAMP_KEY) !== String(now)) return false;
  } catch {
    return false;
  }
  refreshedInThisDocument = true;
  return true;
}

/** Same-origin `/assets/*.js|css` only; query and hash dropped (cache key). */
function assetUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw, window.location.href);
    if (url.origin !== window.location.origin) return null;
    if (!url.pathname.startsWith('/assets/')) return null;
    if (!/\.(js|css)$/.test(url.pathname)) return null;
    return url.origin + url.pathname;
  } catch {
    return null;
  }
}

/**
 * Every build asset this document is known to have touched, **best candidate
 * first** — the order is the whole trick, because the cap cuts the tail.
 *
 * A single `/chat` load pulls ~172 hashed assets, and the chunk that just
 * failed is by construction one of the *last* ones the document asked for. So
 * collection runs newest-first: taking the list in load order and cutting at
 * the cap keeps the app shell and drops exactly the route chunk that is broken
 * — a repair that fetches 80 files and misses the only one that matters.
 *
 * Three sources, none complete on its own: the error message names the failing
 * module in Chrome (never in Safari, where this was found — "Importing a module
 * script failed" and nothing else), resource timings cover lazily imported
 * chunks including the ones served *from* cache, and the DOM covers the entry
 * bundle and its modulepreloads.
 */
function collectAssetUrls(detail?: string): string[] {
  const urls = new Set<string>();
  const add = (raw: string | null | undefined) => {
    const url = assetUrl(raw);
    if (url) urls.add(url);
  };

  // The browser told us which module failed — nothing beats that.
  if (detail) {
    for (const match of detail.matchAll(/https?:\/\/[^\s"')]+/g)) add(match[0]);
  }

  try {
    const timings = performance.getEntriesByType('resource');
    for (let i = timings.length - 1; i >= 0; i -= 1) add(timings[i].name);
  } catch {
    // Resource timing unavailable — the DOM pass below still yields the entry.
  }

  try {
    document.querySelectorAll('script[src]').forEach((el) => add(el.getAttribute('src')));
    document.querySelectorAll('link[href]').forEach((el) => add(el.getAttribute('href')));
  } catch {
    // Nothing to add; an empty list is handled by the caller.
  }

  return [...urls].slice(0, MAX_ASSETS);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Rewrite the cached copies of this document's build assets, then reload.
 *
 * Returns whether a reload was triggered; false means the cooldown declined
 * and the caller should show its error UI.
 *
 * The response body is consumed on purpose — a cache entry is only replaced
 * once the response has been read to the end, so dropping the body would leave
 * exactly the half-written entry we are here to fix.
 */
export async function refreshAssetCacheAndReload(detail?: string): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (!claimRefresh()) return false;

  const urls = collectAssetUrls(detail);
  if (urls.length > 0) {
    const refetch = Promise.allSettled(
      urls.map((url) =>
        fetch(url, { cache: 'reload', credentials: 'same-origin' }).then((res) => res.arrayBuffer())
      )
    );
    await Promise.race([refetch, sleep(REFRESH_DEADLINE_MS)]);
  }

  // Stamp last, so the reload we are about to trigger is the one reported.
  stampReloadReason('asset-cache-refresh', { detail });
  window.location.reload();
  return true;
}
